import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, Input, Modal, ModalFooter, Select } from '../../../components';
import { useConversationStore, useUIStore } from '../../../stores';
import {
  conversationService,
  InitiateConversationInput,
  WhatsAppTemplate,
} from '../services/conversationService';

interface NewConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Prefill the phone field (e.g. opening from the Customers page). */
  initialPhone?: string;
  /** Prefill the name field. */
  initialName?: string;
  /** Called after the first message is sent, with the resolved conversation id. */
  onSent?: (conversationId: string) => void;
}

/**
 * Compose modal for a staff-initiated outbound WhatsApp conversation.
 *
 * WhatsApp only allows free-form text inside the 24h customer-service window.
 * For a cold start (or an expired window) a pre-approved template is required —
 * the backend enforces this, and this modal surfaces the template picker so
 * staff can reach anyone. When template listing isn't available the operator
 * can still type a template name by hand.
 */
export default function NewConversationModal({
  isOpen,
  onClose,
  initialPhone,
  initialName,
  onSent,
}: NewConversationModalProps) {
  const queryClient = useQueryClient();
  const selectConversation = useConversationStore((s) => s.selectConversation);
  const setMobileView = useUIStore((s) => s.setMobileView);

  const [customerPhone, setCustomerPhone] = useState(initialPhone ?? '');
  const [customerName, setCustomerName] = useState(initialName ?? '');
  const [message, setMessage] = useState('');
  const [useTemplate, setUseTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateParams, setTemplateParams] = useState<string[]>([]);

  // Approved templates for the picker (empty list → manual entry fallback).
  const { data: templates } = useQuery({
    queryKey: ['whatsapp-templates'],
    queryFn: () => conversationService.getWhatsAppTemplates(),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const hasTemplateList = !!templates && templates.length > 0;
  const selectedTemplate: WhatsAppTemplate | undefined = useMemo(
    () => templates?.find((t) => t.name === templateName),
    [templates, templateName]
  );
  const paramCount = selectedTemplate?.paramCount ?? 0;

  // Live preview: fill each {{n}} with the staff's value, falling back to the
  // raw placeholder so they can see exactly what the customer will receive.
  const templatePreview = useMemo(() => {
    const body = selectedTemplate?.bodyText;
    if (!body) return '';
    return body.replace(/{{\s*(\d+)\s*}}/g, (_match, n: string) => {
      const value = templateParams[Number(n) - 1]?.trim();
      return value || `{{${n}}}`;
    });
  }, [selectedTemplate, templateParams]);

  // Re-seed the prefilled fields whenever the modal opens (so opening it for a
  // different customer from the Customers page shows their details).
  useEffect(() => {
    if (isOpen) {
      setCustomerPhone(initialPhone ?? '');
      setCustomerName(initialName ?? '');
    }
  }, [isOpen, initialPhone, initialName]);

  const reset = () => {
    setCustomerPhone(initialPhone ?? '');
    setCustomerName(initialName ?? '');
    setMessage('');
    setUseTemplate(false);
    setTemplateName('');
    setTemplateParams([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const mutation = useMutation({
    mutationFn: (input: InitiateConversationInput) =>
      conversationService.initiateConversation(input),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      selectConversation(result.conversationId);
      setMobileView('chat');
      toast.success(
        result.sendMode === 'template' ? 'Template message sent' : 'Message sent'
      );
      onSent?.(result.conversationId);
      handleClose();
    },
    onError: () => {
      // The api client surfaces the backend detail (incl. the "window closed,
      // template required" 422) via toast, so nothing extra needed here.
    },
  });

  const onSubmit = () => {
    const phone = customerPhone.trim();
    if (!phone) {
      toast.error('Enter a phone number in international format (e.g. +15551234567)');
      return;
    }
    if (useTemplate) {
      if (!templateName.trim()) {
        toast.error('Choose or enter a template name');
        return;
      }
    } else if (!message.trim()) {
      toast.error('Enter a message');
      return;
    }

    mutation.mutate({
      customerPhone: phone,
      customerName: customerName.trim() || undefined,
      message: useTemplate ? undefined : message.trim(),
      templateName: useTemplate ? templateName.trim() : undefined,
      templateLanguage: useTemplate ? selectedTemplate?.language : undefined,
      templateParams: useTemplate ? templateParams.slice(0, paramCount) : undefined,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="New conversation" size="md">
      <div className="space-y-4">
        <Input
          label="Phone number"
          type="tel"
          placeholder="+15551234567"
          value={customerPhone}
          onChange={(e) => setCustomerPhone(e.target.value)}
          helperText="International (E.164) format, including country code."
        />
        <Input
          label="Name (optional)"
          type="text"
          placeholder="Customer name"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
        />

        {/* Free-form vs template toggle */}
        <div className="flex gap-2 rounded-xl bg-surface-2 p-1 text-sm">
          <button
            type="button"
            onClick={() => setUseTemplate(false)}
            className={`flex-1 rounded-lg px-3 py-1.5 font-medium transition-colors ${
              !useTemplate ? 'bg-surface text-ink shadow-s' : 'text-ink-2 hover:text-ink'
            }`}
          >
            Message
          </button>
          <button
            type="button"
            onClick={() => setUseTemplate(true)}
            className={`flex-1 rounded-lg px-3 py-1.5 font-medium transition-colors ${
              useTemplate ? 'bg-surface text-ink shadow-s' : 'text-ink-2 hover:text-ink'
            }`}
          >
            Template
          </button>
        </div>

        {!useTemplate ? (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-2">Message</label>
            <textarea
              rows={4}
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="block w-full rounded-xl border border-line bg-surface px-4 py-2.5 text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            <p className="mt-1.5 text-xs text-ink-3">
              Free-form text only works if the customer messaged in the last 24 hours.
              Otherwise use a template.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {hasTemplateList ? (
              <Select
                label="Template"
                value={templateName}
                onChange={(v) => {
                  setTemplateName(v);
                  setTemplateParams([]);
                }}
                placeholder="Choose an approved template"
                options={(templates ?? []).map((t) => ({
                  value: t.name,
                  label: `${t.name} (${t.language})`,
                }))}
              />
            ) : (
              <Input
                label="Template name"
                type="text"
                placeholder="e.g. order_update"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                helperText="Must match an approved template in WhatsApp Manager."
              />
            )}

            {paramCount > 0 && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-ink-2">
                  Template values
                </label>
                {Array.from({ length: paramCount }).map((_, i) => {
                  const example = selectedTemplate?.paramExamples?.[i];
                  return (
                    <Input
                      key={i}
                      label={`Variable {{${i + 1}}}`}
                      type="text"
                      placeholder={example ? `e.g. ${example}` : `{{${i + 1}}}`}
                      value={templateParams[i] ?? ''}
                      onChange={(e) => {
                        const next = [...templateParams];
                        next[i] = e.target.value;
                        setTemplateParams(next);
                      }}
                    />
                  );
                })}
              </div>
            )}

            {selectedTemplate?.bodyText && (
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-3">Preview</label>
                <p className="whitespace-pre-wrap rounded-card bg-surface-2 p-2 text-xs text-ink-2">
                  {templatePreview}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button onClick={onSubmit} isLoading={mutation.isPending}>
          Send
        </Button>
      </ModalFooter>
    </Modal>
  );
}
